import { inject, Injectable } from "@angular/core";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { Observable, of } from "rxjs";
import { CryptoWalletService } from "@main-services/wallet/crypto_wallet.service";
import { map, switchMap, take, tap, withLatestFrom } from "rxjs/operators";
import { config } from "@config";
import { UserService } from "@main-services/user.service";
import { Web3RemoteService } from "../services/web3.remote";
import { IWeb3MigrationEstimationResponse, IWeb3MigrationStatusResponse } from "../interface/web3.remote.interface";
import { Tradable } from "@models/interfaces/tradable";

import { Web3StateService } from "@modules/web3/+state/web3.state.service";
import { Web3MigrationRoutes } from "@modules/web3/w3st-migration.module";
import { Web3NavigationService } from "@modules/web3/services/web3.navigation";
import { Router } from "@angular/router";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { SegmentService } from "@main-services/segment.service";
import { IMergeUrls, IMergeUrlsKeys } from "@modules/web3/entities/merge-urls.interface";
import { PortfolioRemote } from "@modules/portfolio/infrastructure/portfolio.remote";
import { WEB3_FEE_BENEFFIT_FLOW, Web3VSNFeeBenefitFF } from "../interface/web3.benefit.ui.interface";
import { RudderstackDynamicProperties, RudderstackService } from "@main-services/rudderstack.service";

//TODO: move to separate file
export enum WEB3_MERGE_PHASE {
	None = "none",
	Announced = "announced",
	PreMerge = "preMerge",
	Cutoff = "cutoff",
	Merge = "merge",
	PostMerge = "postMerge"
}

export enum Web3TrackingEventNames {
	MigrationIntroScreenViewed = "Web3 Migration Intro Screen Viewed",
	MigrationEstimateScreenViewed = "Web3 Migration Estimate Screen Viewed",
	MigrationSignUpOptInClicked = "Web3 Migration OptIn Button Clicked",
	MigrationSuccessScreenViewed = "Web3 Migration Success Screen Viewed"
}

export interface Web3TokenMergeFF {
	phase: WEB3_MERGE_PHASE;
	mergeDate: string;
	announcementDate: string;
}

export interface IPantosBestAsset {
	asset: Tradable;
	balance: number;
}

@Injectable({ providedIn: "root" })
export class Web3Facade {
	private launchDarklyService: LaunchDarklyService = inject(LaunchDarklyService);
	private cryptoWalletService: CryptoWalletService = inject(CryptoWalletService);
	private userService: UserService = inject(UserService);
	private remote: Web3RemoteService = inject(Web3RemoteService);
	private web3NavigationService: Web3NavigationService = inject(Web3NavigationService);
	private router: Router = inject(Router);
	private tradableListService: TradableListService = inject(TradableListService);
	public web3StateService: Web3StateService = inject(Web3StateService);
	private segmentService = inject(SegmentService);
	private rudderstackService = inject(RudderstackService);
	private portfolioRemote = inject(PortfolioRemote);

	public learnMoreLink = `${config.baseUrl + this.userService.user.getShortLang()}`;
	private learnMoreLoaded = false;

	//adding this as separate link because I presume learnMoreLink might be subject to change
	public baseURLWithLang = `${config.baseUrl + this.userService.user.getShortLang()}`;

	public getMergePhase(): Observable<Web3TokenMergeFF> {
		return this.launchDarklyService.getFeatureConfig$<Web3TokenMergeFF>(LD_FLAG.WEB3_TOKEN_MERGE);
	}

	public getVSNFeeBenefitFF(flow: WEB3_FEE_BENEFFIT_FLOW): Observable<boolean> {
		return this.launchDarklyService.getFeatureConfig$<Web3VSNFeeBenefitFF>(LD_FLAG.WEB3_VSN_FEE_BENEFIT).pipe(
			map((d) => {
				return !!d[flow];
			})
		);
	}

	public trackEvent(model: Web3TrackingEventNames, properties: Partial<RudderstackDynamicProperties>): void {
		this.segmentService.track({
			name: model,
			properties
		});
		this.rudderstackService.track({
			name: model,
			properties
		});
	}

	public userHoldsBestOrPan(): Observable<boolean> {
		return this.getPantosAndBestPid().pipe(
			map(([pan, best]) =>
				this.cryptoWalletService
					.getNotEmptyCryptoCoins()
					.some((coin) => coin.getPid() === pan.pid || coin.getPid() === best.pid)
			),
			tap((hasBestOrPan) => this.web3StateService.setUserHoldsBestOrPan(hasBestOrPan))
		);
	}

	public userHoldsVision(): Observable<boolean> {
		return this.getVisionPid().pipe(
			map((visionPid) =>
				this.cryptoWalletService.getNotEmptyCryptoCoins().some((coin) => coin.getPid() === visionPid)
			)
		);
	}

	public isBestOrPantosPid(pId: string): Observable<boolean> {
		if (!pId) {
			return;
		}
		return this.getPantosAndBestPid().pipe(map(([pan, best]) => pan.pid === pId || best.pid === pId));
	}

	private getPantosAndBestPid(): Observable<[{ pid: string }, { pid: string }]> {
		return this.launchDarklyService
			.getFeatureConfig$<{ pid: string }>(LD_FLAG.PANTOS_PID)
			.pipe(withLatestFrom(this.launchDarklyService.getFeatureConfig$<{ pid: string }>(LD_FLAG.BEST_PID)));
	}

	getMergeUrls(): Observable<IMergeUrls> {
		return this.launchDarklyService.getFeatureConfig$<IMergeUrls>(LD_FLAG.WEB3_TOKEN_MERGE_URLS);
	}

	getSuffixHelper(suffix: IMergeUrlsKeys, urls: IMergeUrls): string {
		const sx = urls[suffix];
		if (!sx) {
			return "";
		}

		return sx;
	}

	public openLearnMore(): void {
		window.open(this.learnMoreLink, "_blank");
	}

	public loadUrls(): void {
		if (!this.learnMoreLoaded) {
			this.getMergeUrls()
				.pipe(take(1))
				.subscribe((urls) => {
					const suffix = this.getSuffixHelper(IMergeUrlsKeys.landingPage, urls);
					this.learnMoreLink += suffix;
					this.learnMoreLoaded = true;
				});
		}
	}

	public getVisionPid(): Observable<string> {
		return this.launchDarklyService
			.getFeatureConfig$<{
				pid: string;
			}>(LD_FLAG.VISION_PID)
			.pipe(map((data) => data.pid));
	}

	getVisionTradable$(): Observable<Tradable> {
		return this.getVisionPid().pipe(
			map((pid) => {
				return this.tradableListService.getItemByPid(pid);
			})
		);
	}

	public getSignUpStatusSig$(): void {
		if (this.web3StateService.$signedUpSignal() === null) {
			this.remote.getWeb3Status().subscribe((status) => {
				this.web3StateService.setSignedUpSignal(status);
			});
		}
	}

	public setSignupStatus(status: IWeb3MigrationStatusResponse): void {
		this.web3StateService.setSignedUpSignal(status);
	}

	getSignUpStatus$(): Observable<IWeb3MigrationStatusResponse> {
		if (this.web3StateService.$signedUpSignal() === null) {
			return this.remote.getWeb3Status().pipe(
				tap((status) => this.web3StateService.setSignedUpSignal(status)),
				map(() => this.web3StateService.$signedUpSignal())
			);
		}
		return of(this.web3StateService.$signedUpSignal());
	}

	getEstimation$(): Observable<IWeb3MigrationEstimationResponse> {
		if (this.web3StateService.$estimateSignal() === null) {
			return this.remote.getWeb3Estimation().pipe(
				tap((estimation) => this.web3StateService.setEstimateSignal(estimation)),
				map(() => this.web3StateService.$estimateSignal())
			);
		}
		return of(this.web3StateService.$estimateSignal());
	}

	invalidationEstimation(): void {
		this.web3StateService.invalidateEstimateSignal();
	}

	invalidationStatus(): void {
		this.web3StateService.invalidateSignedUpSignal();
	}

	getPantosAndBestBalance$(): Observable<IPantosBestAsset[]> {
		return this.getPantosAndBestPid().pipe(
			map(([pan, best]) => {
				return this.cryptoWalletService
					.getAssetsFromWallet()
					.filter((asset) => asset.getPid() === pan.pid || asset.getPid() === best.pid)
					.map((g) => {
						return {
							asset: g,
							balance: this.cryptoWalletService.getBalanceFromAsset(g)
						};
					});
			})
		);
	}

	isSelectedBalanceEnough$(): Observable<boolean> {
		return this.getPantosAndBestBalance$().pipe(
			map((assets) => {
				const sum = assets.reduce((acc, asset) => acc + asset.balance, 0);
				return sum > 0;
			})
		);
	}

	public goToMigrationIntro(): void {
		this.web3NavigationService.gotoWeb3ByUrl(Web3MigrationRoutes.autoMigrationIntro);
	}

	public goToNewToken(newTokenPid: string): void {
		const tradable = this.tradableListService.getItemByPid(newTokenPid);
		this.router.navigate([`/asset/${tradable.getId()}`]);
	}

	// TODO: can use property below to return aprox balance for my vision
	public getVB$(): Observable<string> {
		return this.getVisionPid().pipe(
			switchMap((pid) => {
				return this.portfolioRemote.getAssetDetails(pid).pipe(map((res) => res.asset_balance));
			})
		);
	}

	/**
	 * @description get WS balance status per asset Id
	 * @param pid string
	 * @returns Observable<number>
	 */
	getWalletBalanceStatusHelper$(id: string): Observable<number> {
		return this.cryptoWalletService.getCryptoBalancesStatus$().pipe(
			map((status) => {
				const wallet = this.cryptoWalletService.getWalletsByTradableId(id)[0];
				const blc = status.balances.filter((b) => b.walletGuid === wallet.getId())[0];

				return blc.balance;
			})
		);
	}
}
